import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, ROLE_LABELS, canManage } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar, Settings2, LogOut, User, Ticket, BarChart3, MessageCircle, Sparkles, Mic, PhoneCall } from 'lucide-react';
import { NAV, LOGOUT } from '@/constants/testIds';
import { BrandLogo, BrandBadge } from '@/components/BrandLogo';
import ForcePasswordChange from '@/components/auth/ForcePasswordChange';
import { NotificationBell } from '@/components/notifications/NotificationBell';

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const active = (path) => loc.pathname.startsWith(path);

  // Force password change interstitial — rendered instead of the normal shell
  if (user?.must_change_password) return <ForcePasswordChange />;


  const navBtn = (path) => active(path)
    ? 'hc-nav-active h-8'
    : 'h-8 text-stone-700 hover:bg-[color:var(--hc-primary-50)] hover:text-[color:var(--hc-primary-dark)]';

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col">
      <header className="h-14 bg-white border-b border-stone-200 flex items-center px-4 gap-4 sticky top-0 z-30">
        <Link to="/calendar" className="flex items-center gap-2.5" data-testid={NAV.logo}>
          <BrandBadge size={30} />
          <BrandLogo height={22} className="hidden sm:block" />
        </Link>
        <nav className="flex items-center gap-1 ml-6">
          <Link to="/calendar" data-testid={NAV.calendarLink}>
            <Button variant="ghost" size="sm" className={navBtn('/calendar')}>
              <Calendar className="w-4 h-4 mr-1.5" /> Calendar
            </Button>
          </Link>
          <Link to="/tickets" data-testid="nav-tickets-link">
            <Button variant="ghost" size="sm" className={navBtn('/tickets')}>
              <Ticket className="w-4 h-4 mr-1.5" /> Patients
            </Button>
          </Link>
          {user?.role !== 'audiologist' && (
          <Link to="/reports" data-testid="nav-reports-link">
            <Button variant="ghost" size="sm" className={navBtn('/reports')}>
              <BarChart3 className="w-4 h-4 mr-1.5" /> Reports
            </Button>
          </Link>
          )}
          <Link to="/calls" data-testid="nav-calls-link">
            <Button variant="ghost" size="sm" className={navBtn('/calls')}>
              <PhoneCall className="w-4 h-4 mr-1.5" /> Calls
            </Button>
          </Link>
          <Link to="/comms" data-testid="nav-comms-link">
            <Button variant="ghost" size="sm" className={navBtn('/comms')}>
              <MessageCircle className="w-4 h-4 mr-1.5" /> Comms
            </Button>
          </Link>
          <Link to="/agent" data-testid="nav-agent-link">
            <Button variant="ghost" size="sm" className={navBtn('/agent')}>
              <Sparkles className="w-4 h-4 mr-1.5" /> Assistant
            </Button>
          </Link>
          <Link to="/voice" data-testid="nav-voice-link">
            <Button variant="ghost" size="sm" className={navBtn('/voice')}>
              <Mic className="w-4 h-4 mr-1.5" /> Voice
            </Button>
          </Link>
          {canManage(user?.role) && (
            <Link to="/admin" data-testid={NAV.adminLink}>
              <Button variant="ghost" size="sm" className={navBtn('/admin')}>
                <Settings2 className="w-4 h-4 mr-1.5" /> Admin
              </Button>
            </Link>
          )}
        </nav>
        <div className="flex-1" />
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={NAV.userMenu} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-stone-100">
              <div className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center text-xs font-semibold">
                {(user?.full_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="text-left hidden sm:block leading-tight">
                <div className="text-sm font-semibold">{user?.full_name}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  {ROLE_LABELS[user?.role] || user?.role}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm">{user?.full_name}</div>
              <div className="text-xs text-stone-500">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User className="w-4 h-4 mr-2" /> Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid={LOGOUT.button} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <main className="flex-1 flex flex-col overflow-hidden">
        {children}
      </main>
    </div>
  );
}
